import * as React from 'react';
import { View, type ViewProps } from 'react-native';

import { BrandLabel } from '@/components/brand/brand-label';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';

type PosterMetricProps = Readonly<{
  compact?: boolean;
  label: string;
  value: string;
  unit?: string;
}>;

type PosterTelemetryProps = ViewProps &
  Readonly<{
    compact?: boolean;
    metrics: readonly PosterMetricProps[];
  }>;

function PosterMetric({ compact = false, label, value, unit }: PosterMetricProps) {
  return (
    <View
      className={cn('gap-1', compact ? 'min-w-[98px] grow-0 basis-[45%]' : 'min-w-[132px] flex-1')}
    >
      <BrandLabel>{label}</BrandLabel>
      <View className="flex-row items-end gap-2">
        <Text
          adjustsFontSizeToFit
          className={cn(
            'font-mono font-extrabold leading-none text-white',
            compact ? 'text-[23px]' : 'text-[34px]'
          )}
          minimumFontScale={0.62}
          numberOfLines={1}
        >
          {value}
        </Text>
        {unit ? (
          <Text
            className={cn(
              'pb-1 font-mono font-semibold uppercase leading-none text-white/75',
              compact ? 'text-[10px]' : 'text-sm'
            )}
          >
            {unit}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function PosterTelemetry({ compact = false, metrics, className, ...props }: PosterTelemetryProps) {
  return (
    <View
      className={cn(
        'flex-row flex-wrap',
        compact ? 'gap-x-5 gap-y-4' : 'gap-x-8 gap-y-6',
        className
      )}
      {...props}
    >
      {metrics.map((metric) => (
        <PosterMetric key={metric.label} compact={compact} {...metric} />
      ))}
    </View>
  );
}

export { PosterTelemetry };
